import React from "react";
import { Typography, Box } from "@mui/material";
import SimplePaper from "../../../components/SimplePaper";
import { MathComponent } from "mathjax-react";

const D27fromD1 = () => {
  const title = "Calculation of D27 (Bhamsa/Nakshatramsa) from D1 (Rasi)";

  const body = (
    <>
      <Typography variant="body1" paragraph>
        The D27 chart, or Bhamsa/Nakshatramsa chart, is used in Vedic astrology
        to analyze physical strength, mental resilience, and health. Below is
        the step-by-step process to calculate the D27 chart from the D1 (Rasi)
        chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Procedure to Calculate D27 (Bhamsa) from D1 (Rasi)
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Planet's Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        First, identify the degree of the planet in the D1 chart. For example,
        if Venus is located at 23° Taurus, this degree will be used for the
        calculation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Divide the Sign into Twenty-Seven Parts
      </Typography>

      <Typography variant="body1" paragraph>
        Each zodiac sign is divided into twenty-seven equal parts, each spanning
        1.1111°. To find the Bhamsa position of a planet, we divide its degree
        in the sign by 1.1111.
      </Typography>

      <Box sx={{ textAlign: "center", margin: "1rem 0" }}>
        <MathComponent
          tex={String.raw`Bhamsa \ Position = \frac{Degree \ in \ Sign}{1.1111}`}
        />
      </Box>

      <Typography variant="body1" paragraph>
        Depending on the result, we can determine the section in which the
        planet lies:
      </Typography>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`0 \leq \frac{Degree \ in \ Sign}{1.1111} < 1`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>first 1.1111°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`1 \leq \frac{Degree \ in \ Sign}{1.1111} < 2`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>second 1.1111°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`2 \leq \frac{Degree \ in \ Sign}{1.1111} < 3`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>third 1.1111°</strong>.
        </Typography>
      </Box>

      {/* Add similar blocks for all 27 parts */}

      <Typography variant="h5" gutterBottom>
        3. Assign the Bhamsa Based on the Planet's Degree
      </Typography>

      <Typography variant="body1" paragraph>
        <strong>First 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>same sign</strong> as the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Second 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>2nd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Third 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>3rd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fourth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>4th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fifth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>5th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Sixth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>6th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Seventh 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>7th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Eighth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>8th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Ninth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>9th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Tenth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>10th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Eleventh 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>11th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twelfth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>12th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Thirteenth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>13th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fourteenth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>14th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fifteenth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>15th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Sixteenth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>16th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Seventeenth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>17th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Eighteenth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>18th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Nineteenth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>19th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twentieth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>20th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twenty-first 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>21st sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twenty-second 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>22nd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twenty-third 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>23rd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twenty-fourth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>24th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twenty-fifth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>25th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twenty-sixth 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>26th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twenty-seventh 1.1111°:</strong> The planet is assigned to the{" "}
        <strong>27th sign</strong> from the D1 sign.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Example: Calculating Venus at 23° Taurus
      </Typography>

      <Typography variant="body1" paragraph>
        Taurus is the D1 sign. Venus at 23° falls in the twenty-first 1.1111°
        (between 22.222° and 23.333°). Therefore, it belongs to the{" "}
        <strong>21st sign</strong> from Taurus, which is{" "}
        <strong>Aquarius</strong>
      </Typography>

      <Typography variant="h4" gutterBottom>
        Python Program to Calculate D27 (Bhamsa) from D1
      </Typography>

      <Typography variant="body1" paragraph>
        Below is a Python program that performs the calculation of the D27
        (Bhamsa) position from the D1 (Rasi) chart, using the planet's degree
        and sign in the D1 chart.
      </Typography>

      <Box
        component="pre"
        sx={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <code>
          {`def calculate_bhamsa(degree, sign):
    # List of zodiac signs in order
    zodiac_signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces']

    # Get the index of the current sign in the zodiac cycle
    sign_index = zodiac_signs.index(sign)

    # Determine which Bhamsa the planet is in based on its degree
    if degree <= 1.1111:
        # First 1.1111° belongs to the same sign
        bhamsa_sign = zodiac_signs[sign_index]
    elif 1.1111 < degree <= 2.2222:
        # Second 1.1111° belongs to the 2nd sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    elif 2.2222 < degree <= 3.3333:
        # Third 1.1111° belongs to the 3rd sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 3.3333 < degree <= 4.4444:
        # Fourth 1.1111° belongs to the 4th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 4.4444 < degree <= 5.5555:
        # Fifth 1.1111° belongs to the 5th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 5.5555 < degree <= 6.6666:
        # Sixth 1.1111° belongs to the 6th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 5) % 12]
    elif 6.6666 < degree <= 7.7777:
        # Seventh 1.1111° belongs to the 7th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 6) % 12]
    elif 7.7777 < degree <= 8.8888:
        # Eighth 1.1111° belongs to the 8th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 7) % 12]
    elif 8.8888 < degree <= 9.9999:
        # Ninth 1.1111° belongs to the 9th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 8) % 12]
    elif 9.9999 < degree <= 11.111:
        # Tenth 1.1111° belongs to the 10th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 9) % 12]
    elif 11.111 < degree <= 12.222:
        # Eleventh 1.1111° belongs to the 11th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 10) % 12]
    elif 12.222 < degree <= 13.333:
        # Twelfth 1.1111° belongs to the 12th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 11) % 12]
    elif 13.333 < degree <= 14.444:
        # Thirteenth 1.1111° belongs to the 13th sign from the current sign
        bhamsa_sign = zodiac_signs[sign_index]
    elif 14.444 < degree <= 15.555:
        # Fourteenth 1.1111° belongs to the 14th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    elif 15.555 < degree <= 16.666:
        # Fifteenth 1.1111° belongs to the 15th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 16.666 < degree <= 17.777:
        # Sixteenth 1.1111° belongs to the 16th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 17.777 < degree <= 18.888:
        # Seventeenth 1.1111° belongs to the 17th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 18.888 < degree <= 19.999:
        # Eighteenth 1.1111° belongs to the 18th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 5) % 12]
    elif 19.999 < degree <= 21.111:
        # Nineteenth 1.1111° belongs to the 19th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 6) % 12]
    elif 21.111 < degree <= 22.222:
        # Twentieth 1.1111° belongs to the 20th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 7) % 12]
    elif 22.222 < degree <= 23.333:
        # Twenty-first 1.1111° belongs to the 21st sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 8) % 12]
    elif 23.333 < degree <= 24.444:
        # Twenty-second 1.1111° belongs to the 22nd sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 9) % 12]
    elif 24.444 < degree <= 25.555:
        # Twenty-third 1.1111° belongs to the 23rd sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 10) % 12]
    elif 25.555 < degree <= 26.666:
        # Twenty-fourth 1.1111° belongs to the 24th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 11) % 12]
    elif 26.666 < degree <= 27.777:
        # Twenty-fifth 1.1111° belongs to the 25th sign from the current sign
        bhamsa_sign = zodiac_signs[sign_index]
    elif 27.777 < degree <= 28.888:
        # Twenty-sixth 1.1111° belongs to the 26th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    else:
        # Twenty-seventh 1.1111° belongs to the 27th sign from the current sign
        bhamsa_sign = zodiac_signs[(sign_index + 2) % 12]

    return bhamsa_sign

# Example usage
planet = "Venus"
degree = 23
sign = "Taurus"

bhamsa_sign = calculate_bhamsa(degree, sign)
print(f"{planet} at {degree}° {sign} in the D1 chart is placed in {bhamsa_sign} in the D27 chart.")`}
        </code>
      </Box>

      <Typography variant="body1" paragraph>
        The above Python program calculates the Bhamsa for a planet based on its
        degree and sign in the D1 chart. The program divides the sign into
        twenty-seven equal parts and assigns the Bhamsa based on the planet's
        degree.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Explanation of the Formula Used in the Python Program
      </Typography>

      <Typography variant="body1" paragraph>
        The program divides the sign into twenty-seven parts: the first 1.1111°,
        the second 1.1111°, and so on. It assigns the Bhamsa based on the
        planet's degree within the sign:
        <br />
        - First 1.1111° belongs to the same sign.
        <br />
        - Second 1.1111° belongs to the 2nd sign from the D1 sign.
        <br />
        - Third 1.1111° belongs to the 3rd sign from the D1 sign.
        <br />
        - Fourth 1.1111° belongs to the 4th sign from the D1 sign.
        <br />
        - Fifth 1.1111° belongs to the 5th sign from the D1 sign.
        <br />
        - Sixth 1.1111° belongs to the 6th sign from the D1 sign.
        <br />
        - Seventh 1.1111° belongs to the 7th sign from the D1 sign.
        <br />
        - Eighth 1.1111° belongs to the 8th sign from the D1 sign.
        <br />
        - Ninth 1.1111° belongs to the 9th sign from the D1 sign.
        <br />
        - Tenth 1.1111° belongs to the 10th sign from the D1 sign.
        <br />
        - Eleventh 1.1111° belongs to the 11th sign from the D1 sign.
        <br />
        - Twelfth 1.1111° belongs to the 12th sign from the D1 sign.
        <br />
        - Thirteenth 1.1111° belongs to the 13th sign from the D1 sign.
        <br />
        - Fourteenth 1.1111° belongs to the 14th sign from the D1 sign.
        <br />
        - Fifteenth 1.1111° belongs to the 15th sign from the D1 sign.
        <br />
        - Sixteenth 1.1111° belongs to the 16th sign from the D1 sign.
        <br />
        - Seventeenth 1.1111° belongs to the 17th sign from the D1 sign.
        <br />
        - Eighteenth 1.1111° belongs to the 18th sign from the D1 sign.
        <br />
        - Nineteenth 1.1111° belongs to the 19th sign from the D1 sign.
        <br />
        - Twentieth 1.1111° belongs to the 20th sign from the D1 sign.
        <br />
        - Twenty-first 1.1111° belongs to the 21st sign from the D1 sign.
        <br />
        - Twenty-second 1.1111° belongs to the 22nd sign from the D1 sign.
        <br />
        - Twenty-third 1.1111° belongs to the 23rd sign from the D1 sign.
        <br />
        - Twenty-fourth 1.1111° belongs to the 24th sign from the D1 sign.
        <br />
        - Twenty-fifth 1.1111° belongs to the 25th sign from the D1 sign.
        <br />
        - Twenty-sixth 1.1111° belongs to the 26th sign from the D1 sign.
        <br />- Twenty-seventh 1.1111° belongs to the 27th sign from the D1
        sign.
      </Typography>

      <Typography variant="body1" paragraph>
        This method programmatically reproduces the manual calculation and can
        be easily adjusted for different planets, degrees, and signs.
      </Typography>
    </>
  );

  return <SimplePaper title={title} body={body} />;
};

export default D27fromD1;
